<template>
  <div class="selectable-country-list"
       :element-loading-background="isDarkTheme ? '' : 'rgba(255, 255, 255, 1)'"
       v-loading="loading">
    <div class="search-container">
      <zm-autocomplete
        data-testid="checkout-input-71"
        v-model="regionName"
        :fetch-suggestions="filterCountryList"
        :trigger-on-focus="false"
        aria-autocomplete="false"
        aria-label="Search by Countries/Regions"
        aria-placeholder="Search by Countries/Regions"
        clearable
        :placeholder="this.$t('billing.search_countries')"
        @select="handleRegionSelect"
        label="Search by Countries/Regions"
      >
        <template #prefix><i class="zbo-input__icon zm-icon-search"></i></template>
      </zm-autocomplete>
    </div>
    <div class="countries-container">
      <selection-table
        :data="viewCountryList"
        :height="383"
        :keeps="20"
        :estimate-size="40"
        :item-size="40"
        ref="countriesTable"
        @select="handleSelect"
        @select-all="handleSelectAll"
        :init-selections="initSelections"
        row-key="id"
        stripe
      >
        <zm-virtual-table-column
          type="selection"
          :reserve-selection="true"
          select-key="regionName"
          :selectable="getSelectable"
        >
        </zm-virtual-table-column>
        <template v-if="is_desktop_reactive">
          <zm-virtual-table-column
            prop="regionName"
            :label="$t('billing.countries_regions')"
          >
          </zm-virtual-table-column>
          <zm-virtual-table-column
            :width="130"
            :label="$t('billing.tel.landline')"
          >
            <template #default="scope">
              <span tabindex="0" class="column-span">{{ $money(scope.row.landLineRate, { fixedLength: 4, needRemoveDecimal: false, forceDiff: true }) }}</span>
            </template>
          </zm-virtual-table-column>
          <zm-virtual-table-column
            :width="130"
            :label="$t('billing.tel.mobile')"
          >
            <template #default="scope">
              <span tabindex="0" class="column-span">{{ $money(scope.row.mobileRate, { fixedLength: 4, needRemoveDecimal: false, forceDiff: true }) }}</span>
            </template>
          </zm-virtual-table-column>
        </template>
        <zm-virtual-table-column
          v-else
          label="Select all"
        >
          <template #default="scope">
              <zm-row :gutter="4">
                <zm-col :span="8">Name</zm-col>
                <zm-col :span="16">
                  {{ scope.row.regionName }}
                </zm-col>
              </zm-row>
              <zm-row :gutter="4">
                <zm-col :span="8">{{ $t('billing.tel.landline') }}</zm-col>
                <zm-col :span="16">
                  {{ $money(scope.row.mobileRate, { fixedLength: 4, needRemoveDecimal: false, forceDiff: true }) }}
                </zm-col>
              </zm-row>
              <zm-row :gutter="4">
                <zm-col :span="8">{{ $t('billing.tel.mobile') }}</zm-col>
                <zm-col :span="16">
                  {{ $money(scope.row.mobileRate, { fixedLength: 4, needRemoveDecimal: false, forceDiff: true }) }}
                </zm-col>
              </zm-row>
          </template>
        </zm-virtual-table-column>
      </selection-table>
    </div>
  </div>
</template>
<script>
import tabLoadingMixin from "@/common/mixin/tabLoadingMixin";
import SelectionTable from '@/components/SelectionTable';
import { mapGetters } from 'vuex';
import { THEME } from '@/views/checkout/constant.js';
import { ZmVirtualTableColumn } from '@zoom/zoom-ui-vue3';

export default {
  name: 'SelectableCountryTable',
  mixins: [tabLoadingMixin],
  computed: {
    ...mapGetters(['clientWebViewTheme']),
    isDarkTheme() {
      return this.clientWebViewTheme === THEME.DARK
    },
    viewCountryList() {
      return this.filterRegions.length && this.regionName ? this.filterRegions : this.countryList;
    },
    initSelections() {
      return this.countryList.filter(item => this.selectedCountries.indexOf(item.id) !== -1);
    },
  },
  data() {
    return {
      regionName: '',
      filterRegions: [],
    };
  },
  props: {
    countryList: Array,
    selectedCountries: Array,
    tableTitle: {
      type: String,
      default: ''
    },
  },
  mounted() {
    this.$nextTick(() => {
      const header = this.$el.querySelector('th[role="columnheader"]:first-child');
      if (header) {
        header.setAttribute('aria-label', this.tableTitle);
      }
    });
  },
  methods: {
    filterCountryList(qs) {
      if (!qs || qs.trim().length === 0) {
        return [];
      }

      const countryList = this.countryList || [];
      const filteredResults = countryList.filter(this.createFilter(qs));

      const results = filteredResults.map(item => ({
        label: item.regionName,
        value: item
      }));

      return results;
    },
    createFilter(qs) {
      return country => {
        return country.regionName.toLowerCase().indexOf(qs.toLowerCase()) !== -1;
      };
    },
    handleRegionSelect(selectedItem) {
      const region = selectedItem.value || selectedItem;
      this.filterRegions = [region];
      this.regionName = region.regionName;
    },
    handleSelectAll(value) {
      this.$emit('select-all', value);
      this.handleSelectionChange(value);
    },
    handleSelect(selectList, value) {
      const isSelected = !!selectList.find(item => item.id === value.id);
      this.$emit('select', { ...value, isSelected });
      this.handleSelectionChange(selectList);
    },
    handleSelectionChange(value) {
      this.$emit('update-countries', value.map(item => item.id));
    },
    getSelectable(row) {
      return !row.disabled;
    },
  },
  components: {
    SelectionTable,
    ZmVirtualTableColumn,
  },
};
</script>
<style lang="scss" scoped>
@import '@zoom/billing-responsive/styles/responsive.scss';

.countries-container {
  margin-top: 16px;
  width: 100%;
}

@include media(mobile,tablet){
  :deep .zbo-table-column--selection{
    vertical-align: top !important;
  }
  :deep .zbo-table__header thead tr th{
    background: #F7F9FA;
  }
  :deep .zbo-row .zbo-col:last-child{
    color: var(--zoom-color-text-strong-neutral, #555B62);
  }
}

</style>
<style lang="scss">
@import '@zoom/billing-responsive/styles/responsive.scss';
.selectable-country-list {

  .column-span{
    display: block;
    width: 100%;
    height: 100%;
    padding: 12px;
    margin: -12px;
  }

  .zbo-input__leading {
    top: auto;
  }
  .search-container {
    margin-top: 16px;
  }

  .zbo-autocomplete {
    width: 100%;
  }

  .zbo-input__inner:focus {
    box-shadow: unset;
  }

  .zbo-checkbox {
    line-height: 16px;
    padding: 0;
  }

  .zbo-checkbox .zbo-checkbox-wrap {
    min-height: 16px;
    line-height: 16px;
  }

  .zbo-checkbox__inner {
    top: 4px;
  }
}

.is-keyboard-event {
  .selectable-country-list *:focus {
    box-shadow: 0 0 2px 2px #0e72ed inset;
  }
}
</style>
